// src/components/PrintDesigner/printTemplates.js
// شكل التصميم (layoutJson v2) + القوالب الجاهزة + تحويل التصاميم القديمة
//
// {
//   version: 2,
//   page:  { size: 'A4'|'A5'|'80mm'|'58mm', margin: mm, fontSize: px },
//   bands: { header: { height: mm }, footer: { height: mm } },
//   table: { columns: [...], fontSize, headerBg, headerColor, border, zebra, bilingual, showDescription },
//   elements: [{ id, band: 'header'|'footer', type, x, y, w, h, z, locked, props }]
// }
// العناصر تُرسم بالملّيمتر داخل رأس الصفحة أو ذيلها، وجدول الأصناف بينهما يتمدد حسب عدد الأصناف.

export const PAPER = {
  A4: { w: 210, h: 297, label: "A4", thermal: false },
  A5: { w: 148, h: 210, label: "A5", thermal: false },
  "80mm": { w: 80, h: null, label: "حراري 80mm", thermal: true },
  "58mm": { w: 58, h: null, label: "حراري 58mm", thermal: true },
};

export const contentWidth = (layout) => (PAPER[layout.page.size]?.w ?? 210) - 2 * (layout.page.margin ?? 8);

export const DEFAULT_COLUMNS = [
  { key: "index", labelAr: "#", labelEn: "#", width: 5, visible: true, align: "center" },
  { key: "item", labelAr: "الصنف", labelEn: "Item", width: 30, visible: true, align: "right" },
  { key: "unit", labelAr: "الوحدة", labelEn: "Unit", width: 9, visible: true, align: "center" },
  { key: "quantity", labelAr: "الكمية", labelEn: "Qty", width: 8, visible: true, align: "center" },
  { key: "price", labelAr: "السعر", labelEn: "Price", width: 10, visible: true, align: "center" },
  { key: "total", labelAr: "المجموع", labelEn: "Total", width: 10, visible: true, align: "center" },
  { key: "discount", labelAr: "الخصم", labelEn: "Discount", width: 9, visible: true, align: "center" },
  { key: "tax", labelAr: "الضريبة", labelEn: "VAT", width: 9, visible: true, align: "center" },
  { key: "net", labelAr: "الصافي", labelEn: "Net", width: 10, visible: true, align: "center" },
  { key: "description", labelAr: "الوصف", labelEn: "Description", width: 15, visible: false, align: "right" },
];

export const TOTAL_ROWS = [
  { key: "subtotal", labelAr: "الإجمالي", labelEn: "Subtotal", visible: true },
  { key: "discount", labelAr: "الخصم", labelEn: "Discount", visible: true },
  { key: "afterDiscount", labelAr: "بعد الخصم", labelEn: "After discount", visible: false },
  { key: "vat", labelAr: "ضريبة القيمة المضافة 15%", labelEn: "VAT 15%", visible: true },
  { key: "net", labelAr: "الصافي", labelEn: "Net Total", visible: true },
  { key: "paid", labelAr: "المدفوع", labelEn: "Paid", visible: false },
  { key: "remaining", labelAr: "المتبقي", labelEn: "Remaining", visible: false },
];

// الخصائص الافتراضية لكل نوع عنصر
export const ELEMENT_DEFAULTS = {
  text: {
    w: 60, h: 8,
    props: { text: "نص جديد", fontSize: 12, bold: false, italic: false, underline: false, align: "right", vAlign: "center",
      color: "#111827", bg: "", borderWidth: 0, borderColor: "#111827", borderStyle: "solid", radius: 0, padding: 1, dir: "rtl", lineHeight: 1.5 },
  },
  image: { w: 35, h: 22, props: { src: "", source: "upload", fit: "contain", radius: 0 } },
  logo: { w: 35, h: 22, props: { source: "companyLogo", fit: "contain", radius: 0 } },
  qr: { w: 28, h: 28, props: {} },
  line: { w: 80, h: 1, props: { orientation: "horizontal", thickness: 1, color: "#111827", style: "solid" } },
  rect: { w: 60, h: 25, props: { borderWidth: 1, borderColor: "#111827", borderStyle: "solid", bg: "", radius: 2 } },
  totals: {
    w: 80, h: 34,
    props: { rows: TOTAL_ROWS.map(r => ({ ...r })), fontSize: 11, bilingual: true, netBg: "#111827", netColor: "#ffffff", border: true },
  },
  html: { w: 80, h: 20, props: { html: "<div>{CompanyName}</div>" } },
};

let seq = 0;
export const newId = () => `el_${Date.now().toString(36)}_${(seq++).toString(36)}`;

export function makeElement(type, band, x, y, overrides = {}) {
  const d = ELEMENT_DEFAULTS[type] || ELEMENT_DEFAULTS.text;
  const { props, ...rest } = overrides;
  return { id: newId(), band, type, x, y, w: d.w, h: d.h, z: 1, locked: false, ...rest, props: { ...d.props, ...(props || {}) } };
}

const T = (band, x, y, w, h, text, props = {}) => makeElement("text", band, x, y, { w, h, props: { text, ...props } });

// ───────────────────────── قالب A4 ─────────────────────────
export function a4Template(size = "A4") {
  const W = PAPER[size].w - 16; // عرض المحتوى (هوامش 8mm)
  const half = W / 2;
  return {
    version: 2,
    page: { size, margin: 8, fontSize: 11 },
    bands: { header: { height: 84 }, footer: { height: 64 } },
    table: { columns: DEFAULT_COLUMNS.map(c => ({ ...c })), fontSize: 10, headerBg: "#f1f5f9", headerColor: "#111827", border: "grid", zebra: true, bilingual: true, showDescription: true },
    elements: [
      // ترويسة المنشأة: عربي يمين — شعار وسط — إنجليزي يسار
      T("header", W - 78, 0, 78, 24, "{CompanyName}\nالرقم الضريبي: {CompanyTax}\nس.ت: {CompanyCR}\n{CompanyAddress}", { fontSize: 10, lineHeight: 1.45, vAlign: "top" }),
      makeElement("logo", "header", half - 18, 0, { w: 36, h: 22 }),
      T("header", 0, 0, 78, 24, "{CompanyNameEn}\nVAT: {CompanyTax}\nCR: {CompanyCR}\n{CompanyAddressEn}", { fontSize: 10, lineHeight: 1.45, align: "left", dir: "ltr", vAlign: "top" }),
      makeElement("line", "header", 0, 26, { w: W, h: 1, props: { thickness: 2, color: "#111827" } }),
      T("header", half - 40, 29, 80, 10, "{InvoiceTitle}", { fontSize: 18, bold: true, align: "center" }),
      // بيانات العميل
      makeElement("rect", "header", W - 100, 42, { w: 100, h: 38, props: { radius: 2, borderColor: "#cbd5e1" } }),
      T("header", W - 98, 43, 96, 36, "العميل: {CustomerName}\nالرقم الضريبي: {CustomerTax}\nالجوال: {CustomerMobile}\nالعنوان: {CustomerAddress}", { fontSize: 10.5, lineHeight: 1.75, vAlign: "top", padding: 1.5 }),
      // QR + بيانات الفاتورة
      makeElement("qr", "header", W - 134, 44, { w: 32, h: 32 }),
      makeElement("rect", "header", 0, 42, { w: 58, h: 38, props: { radius: 2, borderColor: "#cbd5e1" } }),
      T("header", 1, 43, 56, 36, "رقم الفاتورة: {InvoiceNumber}\nالتاريخ: {IssueDate} {IssueTime}\nالمرجع: {ReferenceNumber}\nالدفع: {PaymentMethod}", { fontSize: 10.5, lineHeight: 1.75, vAlign: "top", padding: 1.5 }),
      // الذيل
      makeElement("totals", "footer", 0, 3, { w: 82, h: 34 }),
      T("footer", 86, 3, W - 86, 9, "{NetTotalWords}", { fontSize: 10.5, bold: true, bg: "#f8fafc", borderWidth: 1, borderColor: "#cbd5e1", radius: 2, padding: 1.5 }),
      T("footer", 86, 15, W - 86, 20, "ملاحظات: {Notes}", { fontSize: 10, vAlign: "top", color: "#475569" }),
      T("footer", W - 55, 46, 55, 12, "المستلم\n.............................", { fontSize: 10, align: "center", lineHeight: 1.8 }),
      T("footer", half - 27, 46, 55, 12, "البائع: {Salesman}\n.............................", { fontSize: 10, align: "center", lineHeight: 1.8 }),
      T("footer", 0, 46, 55, 12, "الختم\n.............................", { fontSize: 10, align: "center", lineHeight: 1.8 }),
    ],
  };
}

// ───────────────────────── القالب الشامل A4 (الافتراضي) ─────────────────────────
// كل بيانات المنشأة والعميل من بطاقتيهما — الأسطر الفارغة تختفي تلقائيًا عند الطباعة
export function fullTemplate() {
  const W = 194; // A4 بهوامش 8mm
  const box = (x, y, w, h) => makeElement("rect", "header", x, y, { w, h, props: { radius: 2, borderColor: "#cbd5e1", borderWidth: 1 } });
  const bar = (x, y, w, text) => T("header", x, y, w, 7, text, { fontSize: 10, bold: true, align: "center", bg: "#eef2ff", color: "#3730a3", radius: 2 });
  const BUYER_W = 98, INFO_W = 62;
  return {
    version: 2,
    page: { size: "A4", margin: 8, fontSize: 11 },
    bands: { header: { height: 102 }, footer: { height: 72 } },
    table: { columns: DEFAULT_COLUMNS.map(c => ({ ...c })), fontSize: 9.5, headerBg: "#eef2ff", headerColor: "#1e1b4b", border: "grid", zebra: true, bilingual: true, showDescription: true },
    elements: [
      // ── ترويسة المنشأة
      T("header", W - 80, 0, 80, 30,
        "{CompanyName}\nالرقم الضريبي: {CompanyTax}\nالسجل التجاري: {CompanyCR}\n{CompanyFullAddress}\nهاتف: {CompanyPhone}  {CompanyMobile}",
        { fontSize: 9.5, lineHeight: 1.45, vAlign: "top" }),
      makeElement("logo", "header", W / 2 - 17, 0, { w: 34, h: 26 }),
      T("header", 0, 0, 80, 30,
        "{CompanyNameEn}\nVAT No: {CompanyTax}\nCR No: {CompanyCR}\n{CompanyAddressEn}\n{CompanyEmail}\n{CompanyWebsite}",
        { fontSize: 9.5, lineHeight: 1.45, vAlign: "top", align: "left", dir: "ltr" }),
      makeElement("line", "header", 0, 32, { w: W, h: 1, props: { thickness: 2, color: "#1e1b4b" } }),
      T("header", W / 2 - 50, 34, 100, 10, "{InvoiceTitle}", { fontSize: 17, bold: true, align: "center", color: "#1e1b4b" }),

      // ── بيانات العميل (المشتري)
      box(W - BUYER_W, 47, BUYER_W, 52),
      bar(W - BUYER_W + 1, 48, BUYER_W - 2, "بيانات العميل  Buyer"),
      T("header", W - BUYER_W + 1, 56, BUYER_W - 2, 42,
        "الاسم: {CustomerName}\n\u2066Name: {CustomerNameEn}\u2069\nرقم العميل: {CustomerCode}\nالرقم الضريبي: {CustomerTax}\nالسجل التجاري: {CustomerCR}\nالعنوان: {CustomerFullAddress}\nالرقم الإضافي: {CustomerAdditionalNo}\nالجوال: {CustomerMobile}  {CustomerPhone}\nالبريد: {CustomerEmail}",
        { fontSize: 9.5, lineHeight: 1.55, vAlign: "top", padding: 1.5 }),

      // ── بيانات الفاتورة
      box(0, 47, INFO_W, 52),
      bar(1, 48, INFO_W - 2, "بيانات الفاتورة  Invoice"),
      T("header", 1, 56, INFO_W - 2, 42,
        "رقم الفاتورة: {InvoiceNumber}\nالتاريخ: {IssueDate}\nالوقت: {IssueTime}\nالمرجع: {ReferenceNumber}\nطريقة الدفع: {PaymentMethod}\nالبائع: {Salesman}\nالفرع: {BranchName}\nالمستودع: {StockName}",
        { fontSize: 9.5, lineHeight: 1.55, vAlign: "top", padding: 1.5 }),

      // ── QR بين الصندوقين
      makeElement("qr", "header", INFO_W + 3, 54, { w: 28, h: 28 }),
      T("header", INFO_W + 1, 83, 32, 8, "رمز الفاتورة\nالإلكترونية", { fontSize: 7.5, align: "center", color: "#64748b", lineHeight: 1.3 }),

      // ── الذيل
      makeElement("totals", "footer", 0, 3, { w: 84, h: 42, props: { netBg: "#1e1b4b",
        rows: TOTAL_ROWS.map(r => ({ ...r, visible: ["subtotal", "discount", "afterDiscount", "vat", "net"].includes(r.key) })) } }),
      T("footer", 88, 3, W - 88, 9, "{NetTotalWords}", { fontSize: 10, bold: true, bg: "#f8fafc", borderWidth: 1, borderColor: "#cbd5e1", radius: 2, padding: 1.5 }),
      T("footer", 88, 14, W - 88, 8, "الرصيد السابق للعميل: {CustomerPrevBalance}", { fontSize: 9.5, color: "#475569" }),
      T("footer", 88, 22, W - 88, 22, "ملاحظات: {Notes}", { fontSize: 9.5, vAlign: "top", color: "#475569" }),
      T("footer", W - 55, 48, 55, 12, "المستلم\n.............................", { fontSize: 9.5, align: "center", lineHeight: 1.8 }),
      T("footer", W / 2 - 27, 48, 55, 12, "البائع: {Salesman}\n.............................", { fontSize: 9.5, align: "center", lineHeight: 1.8 }),
      T("footer", 0, 48, 55, 12, "الختم\n.............................", { fontSize: 9.5, align: "center", lineHeight: 1.8 }),
      makeElement("line", "footer", 0, 63, { w: W, h: 1, props: { thickness: 1, color: "#cbd5e1" } }),
      T("footer", 0, 64.5, W, 6, "{CompanyFullAddress}   •   {CompanyPhone}   •   {CompanyEmail}   •   {CompanyWebsite}", { fontSize: 8, align: "center", color: "#64748b" }),
    ],
  };
}

// ───────────────────────── قالب حراري 80mm ─────────────────────────
export function thermalTemplate(size = "80mm") {
  const W = PAPER[size].w - 6;
  const cols = ["item", "quantity", "price", "net"];
  return {
    version: 2,
    page: { size, margin: 3, fontSize: 10 },
    bands: { header: { height: 62 }, footer: { height: 70 } },
    table: {
      columns: DEFAULT_COLUMNS.map(c => ({ ...c, visible: cols.includes(c.key), width: { item: 46, quantity: 14, price: 18, net: 22 }[c.key] ?? c.width })),
      fontSize: 9, headerBg: "#111827", headerColor: "#ffffff", border: "rows", zebra: false, bilingual: false, showDescription: false,
    },
    elements: [
      makeElement("logo", "header", W / 2 - 13, 0, { w: 26, h: 16 }),
      T("header", 0, 17, W, 7, "{CompanyName}", { fontSize: 13, bold: true, align: "center" }),
      T("header", 0, 24, W, 10, "الرقم الضريبي: {CompanyTax}\n{CompanyAddress}", { fontSize: 8.5, align: "center", lineHeight: 1.4 }),
      makeElement("line", "header", 0, 35, { w: W, h: 1, props: { style: "dashed" } }),
      T("header", 0, 37, W, 6, "{InvoiceTitle}", { fontSize: 11, bold: true, align: "center" }),
      T("header", 0, 44, W, 16, "رقم: {InvoiceNumber}\nالتاريخ: {IssueDate} {IssueTime}\nالعميل: {CustomerName}", { fontSize: 9, lineHeight: 1.6, vAlign: "top" }),
      makeElement("totals", "footer", 0, 2, { w: W, h: 30, props: { fontSize: 9.5, bilingual: false, rows: TOTAL_ROWS.map(r => ({ ...r, visible: ["subtotal", "discount", "vat", "net"].includes(r.key) })) } }),
      makeElement("qr", "footer", W / 2 - 15, 34, { w: 30, h: 30 }),
      T("footer", 0, 65, W, 5, "شكرًا لزيارتكم", { fontSize: 9, align: "center", bold: true }),
    ],
  };
}

export const TEMPLATES = [
  { key: "full", label: "فاتورة ضريبية شاملة A4", make: () => fullTemplate() },
  { key: "a4", label: "فاتورة A4", make: () => a4Template("A4") },
  { key: "a5", label: "فاتورة A5", make: () => a4Template("A5") },
  { key: "80mm", label: "إيصال حراري 80mm", make: () => thermalTemplate("80mm") },
  { key: "58mm", label: "إيصال حراري 58mm", make: () => thermalTemplate("58mm") },
];

export const templateForPaper = (size) =>
  PAPER[size]?.thermal ? thermalTemplate(size) : size === "A5" ? a4Template("A5") : fullTemplate();


// ======================================================================
// قوالب السندات (قبض / صرف) — بدون جدول أصناف، كل المحتوى في رأس الصفحة
// ======================================================================
const VT = (x, y, w, h, text, props = {}) => makeElement("text", "header", x, y, { w, h, props: { text, ...props } });
const noTable = (fontSize = 10) => ({ columns: DEFAULT_COLUMNS.map(c => ({ ...c })), fontSize, headerBg: "#f1f5f9", headerColor: "#111827", border: "grid", zebra: false, bilingual: false, showDescription: false, enabled: false });

/** سند A4 / A5 — size: "A4" | "A5" */
export function voucherTemplate(size = "A4") {
  const a5 = size === "A5";
  const W = PAPER[size].w - 16;
  const k = a5 ? 0.78 : 1;           // تصغير الخطوط للـ A5
  const fs = (n) => Math.round(n * k * 10) / 10;
  const half = W / 2;
  const box = (x, y, w, h, extra = {}) => makeElement("rect", "header", x, y, { w, h, props: { radius: 2, borderColor: "#cbd5e1", borderWidth: 1, ...extra } });
  let y = 0;
  const els = [
    VT(W - (a5 ? 62 : 80), 0, a5 ? 62 : 80, a5 ? 22 : 26, "{CompanyName}\nالرقم الضريبي: {CompanyTax}\nس.ت: {CompanyCR}\n{CompanyFullAddress}", { fontSize: fs(9.5), lineHeight: 1.45, vAlign: "top" }),
    makeElement("logo", "header", half - (a5 ? 14 : 17), 0, { w: a5 ? 28 : 34, h: a5 ? 20 : 24 }),
    VT(0, 0, a5 ? 62 : 80, a5 ? 22 : 26, "{CompanyNameEn}\nVAT: {CompanyTax}\n{CompanyPhone}\n{CompanyEmail}", { fontSize: fs(9.5), lineHeight: 1.45, vAlign: "top", align: "left", dir: "ltr" }),
  ];
  y = a5 ? 24 : 28;
  els.push(makeElement("line", "header", 0, y, { w: W, h: 1, props: { thickness: 2, color: "#1e1b4b" } }));
  y += 3;
  // العنوان + الرقم والتاريخ
  els.push(box(0, y, W, a5 ? 13 : 15, { borderColor: "#1e1b4b", borderWidth: 1.5 }));
  els.push(VT(W - 100, y + 1, 99, a5 ? 11 : 13, "{VoucherType}", { fontSize: fs(18), bold: true, color: "#1e1b4b" }));
  els.push(VT(1, y + 1, a5 ? 60 : 78, a5 ? 11 : 13, "رقم السند: {VoucherNumber}\nالتاريخ: {VoucherDate}", { fontSize: fs(11), bold: true, lineHeight: 1.5, align: "left" }));
  y += a5 ? 17 : 20;
  // الطرف
  els.push(VT(0, y, W, a5 ? 8 : 9, "{PartyLabel} {PartyName}", { fontSize: fs(13), bold: true, borderWidth: 0 }));
  els.push(makeElement("line", "header", 0, y + (a5 ? 8 : 9), { w: W, h: 1, props: { style: "dotted", color: "#475569" } }));
  y += a5 ? 11 : 13;
  // المبلغ
  els.push(VT(W - (a5 ? 52 : 62), y, a5 ? 52 : 62, a5 ? 13 : 15, "مبلغ وقدره\n{VoucherTotal} ر.س", { fontSize: fs(15), bold: true, align: "center", bg: "#eef2ff", color: "#1e1b4b", borderWidth: 1, borderColor: "#1e1b4b", radius: 2, lineHeight: 1.35 }));
  els.push(VT(0, y, W - (a5 ? 55 : 66), a5 ? 13 : 15, "{VoucherTotalWords}", { fontSize: fs(11.5), bold: true, borderWidth: 1, borderColor: "#cbd5e1", radius: 2, padding: 1.5 }));
  y += a5 ? 17 : 20;
  // التفاصيل
  els.push(box(0, y, W, a5 ? 22 : 26));
  els.push(VT(half + 1, y + 1, half - 2, a5 ? 20 : 24, "طريقة الدفع: {VoucherPayment}\n{VoucherSafeOrBankLabel}: {VoucherSafeOrBank}\nالفرع: {BranchName}", { fontSize: fs(10.5), lineHeight: 1.7, vAlign: "top", padding: 1.5 }));
  els.push(VT(1, y + 1, half - 2, a5 ? 20 : 24, "رقم المرجع: {VoucherReference}\nتاريخ المرجع: {VoucherReferenceDate}\nقبل الضريبة: {VoucherNet}\nالضريبة: {VoucherTax}", { fontSize: fs(10.5), lineHeight: 1.7, vAlign: "top", padding: 1.5 }));
  y += a5 ? 25 : 30;
  // البيان
  els.push(VT(0, y, W, a5 ? 18 : 24, "وذلك عن:\n{VoucherDescription}", { fontSize: fs(11), vAlign: "top", borderWidth: 1, borderColor: "#cbd5e1", radius: 2, padding: 2, lineHeight: 1.7 }));
  y += a5 ? 22 : 30;
  // التوقيعات
  const sw = (W - 8) / 3;
  ["المحاسب", "{SignatureMiddle}", "{SignatureParty}"].forEach((label, i) => {
    els.push(VT(W - sw * (i + 1) - 4 * i, y, sw, a5 ? 12 : 15, `${label}\n..............................`, { fontSize: fs(10.5), align: "center", bold: true, lineHeight: a5 ? 1.8 : 2.2 }));
  });
  y += a5 ? 14 : 18;
  return {
    version: 2,
    page: { size, margin: 8, fontSize: 11 },
    bands: { header: { height: y }, footer: { height: 8 } },
    table: noTable(),
    elements: [
      ...els,
      makeElement("text", "footer", 0, 1, { w: W, h: 5, props: { text: "طُبع في {PrintDate} — بواسطة {User}", fontSize: 8, color: "#94a3b8", align: "left", dir: "rtl" } }),
    ],
  };
}

/** سند حراري 80mm / 58mm */
export function voucherThermalTemplate(size = "80mm") {
  const W = PAPER[size].w - 6;
  const els = [
    makeElement("logo", "header", W / 2 - 12, 0, { w: 24, h: 15 }),
    VT(0, 16, W, 7, "{CompanyName}", { fontSize: 12, bold: true, align: "center" }),
    VT(0, 23, W, 5, "الرقم الضريبي: {CompanyTax}", { fontSize: 8.5, align: "center" }),
    makeElement("line", "header", 0, 29, { w: W, h: 1, props: { style: "dashed" } }),
    VT(0, 31, W, 7, "{VoucherType}", { fontSize: 12, bold: true, align: "center" }),
    VT(0, 38, W, 11, "رقم: {VoucherNumber}\nالتاريخ: {VoucherDate} {VoucherTime}", { fontSize: 9, lineHeight: 1.6, vAlign: "top" }),
    makeElement("line", "header", 0, 50, { w: W, h: 1, props: { style: "dashed" } }),
    VT(0, 52, W, 12, "{PartyLabel}\n{PartyName}", { fontSize: 10, bold: true, lineHeight: 1.5, vAlign: "top" }),
    VT(0, 65, W, 11, "{VoucherTotal} ر.س", { fontSize: 17, bold: true, align: "center", bg: "#111827", color: "#ffffff", radius: 1.5 }),
    VT(0, 77, W, 12, "{VoucherTotalWords}", { fontSize: 8.5, align: "center", lineHeight: 1.45, vAlign: "top" }),
    VT(0, 90, W, 14, "الدفع: {VoucherPayment} — {VoucherSafeOrBank}\nالبيان: {VoucherDescription}", { fontSize: 9, lineHeight: 1.6, vAlign: "top" }),
    makeElement("line", "header", 0, 105, { w: W, h: 1, props: { style: "dashed" } }),
    VT(0, 107, W, 12, "التوقيع\n..............................", { fontSize: 9, align: "center", lineHeight: 1.8 }),
  ];
  return {
    version: 2,
    page: { size, margin: 3, fontSize: 10 },
    bands: { header: { height: 121 }, footer: { height: 5 } },
    table: noTable(9),
    elements: els,
  };
}

export const VOUCHER_TEMPLATES = [
  { key: "v-a4", label: "سند A4", make: () => voucherTemplate("A4") },
  { key: "v-a5", label: "سند A5 (نصف صفحة)", make: () => voucherTemplate("A5") },
  { key: "v-80mm", label: "سند حراري 80mm", make: () => voucherThermalTemplate("80mm") },
  { key: "v-58mm", label: "سند حراري 58mm", make: () => voucherThermalTemplate("58mm") },
];

export const voucherTemplateForPaper = (size) =>
  PAPER[size]?.thermal ? voucherThermalTemplate(size) : voucherTemplate(size === "A5" ? "A5" : "A4");

/** نوع المستند: "invoice" | "voucher" — السندات أرقامها 101..199 */
export const isVoucherType = (typeId) => Number(typeId) >= 101 && Number(typeId) <= 199;
export const templatesFor = (kind) => (kind === "voucher" ? VOUCHER_TEMPLATES : TEMPLATES);
export const templateForPaperOf = (kind, size) => (kind === "voucher" ? voucherTemplateForPaper(size) : templateForPaper(size));

/**
 * يحوّل التصميم المحفوظ إلى الشكل الجديد:
 *   - v2 يُرجع كما هو (مع إكمال الحقول الناقصة)
 *   - القديم (headerHtml/footerHtml/columns) يُبنى من القالب المناسب مع نقل الأعمدة والـ HTML
 */
export function normalizeLayout(saved) {
  if (saved?.version === 2) {
    const base = saved.table?.enabled === false ? voucherTemplateForPaper(saved.page?.size) : templateForPaper(saved.page?.size);
    return {
      ...base, ...saved,
      page: { ...base.page, ...saved.page },
      bands: { header: { ...base.bands.header, ...saved.bands?.header }, footer: { ...base.bands.footer, ...saved.bands?.footer } },
      table: { ...base.table, ...saved.table, columns: mergeColumns(saved.table?.columns) },
      elements: Array.isArray(saved.elements) ? saved.elements : [],
    };
  }

  const layout = templateForPaper(saved?.paperSize || "A4");
  if (Array.isArray(saved?.columns)) {
    layout.table.columns = mergeColumns(saved.columns.slice().sort((a, b) => (a.order || 99) - (b.order || 99)).map(c => ({ ...c, width: parseFloat(c.width) || undefined })));
  }
  const W = contentWidth(layout);
  if (saved?.headerHtml?.trim()) {
    layout.bands.header.height += 22;
    layout.elements.push(makeElement("html", "header", 0, layout.bands.header.height - 21, { w: W, h: 20, props: { html: saved.headerHtml } }));
  }
  if (saved?.footerHtml?.trim()) {
    layout.bands.footer.height += 22;
    layout.elements.push(makeElement("html", "footer", 0, layout.bands.footer.height - 21, { w: W, h: 20, props: { html: saved.footerHtml } }));
  }
  if (saved?.showCompanyHeader === false) {
    // كان المستخدم يخفي ترويسة الشركة — نزيل عناصرها من القالب
    layout.elements = layout.elements.filter(e => !(e.band === "header" && e.y < 26) || e.type === "html");
  }
  if (saved?.customCss) layout.customCss = saved.customCss;
  return layout;
}

function mergeColumns(cols) {
  if (!Array.isArray(cols) || !cols.length) return DEFAULT_COLUMNS.map(c => ({ ...c }));
  const known = cols
    .filter(c => DEFAULT_COLUMNS.some(d => d.key === c.key))
    .map(c => ({ ...DEFAULT_COLUMNS.find(d => d.key === c.key), ...c, width: Number(c.width) || DEFAULT_COLUMNS.find(d => d.key === c.key).width }));
  const missing = DEFAULT_COLUMNS.filter(d => !known.some(c => c.key === d.key)).map(c => ({ ...c, visible: false }));
  return [...known, ...missing];
}

// ======================================================================
// عدة قوالب لنفس نوع الفاتورة — تُحفظ كلها في layoutJson واحد (بدون تعديل الـ API)
// { version: 3, templates: [{ id, name, isDefault, layout }] }
// ======================================================================
export const tplId = () => `tpl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

export const paperLabel = (layout) => PAPER[layout?.page?.size]?.label || "A4";

/** يقبل أي صيغة محفوظة (v3 / v2 / القديمة / نص JSON) ويرجع قائمة القوالب */
export function parseTemplateSet(saved) {
  let s = saved;
  if (typeof s === "string") {
    try { s = JSON.parse(s); } catch { s = null; }
  }
  if (!s || typeof s !== "object") return [];
  let list;
  if (s.version === 3 && Array.isArray(s.templates)) {
    list = s.templates
      .filter(t => t && t.layout)
      .map((t, i) => ({ id: t.id || tplId(), name: t.name || `قالب ${i + 1}`, isDefault: !!t.isDefault, layout: normalizeLayout(t.layout) }));
  } else {
    const layout = normalizeLayout(s);
    list = [{ id: tplId(), name: `فاتورة ${paperLabel(layout)}`, isDefault: true, layout }];
  }
  if (list.length && !list.some(t => t.isDefault)) list[0] = { ...list[0], isDefault: true };
  return list;
}

export const serializeTemplateSet = (templates) =>
  JSON.stringify({ version: 3, templates: templates.map(t => ({ ...t, layout: { ...t.layout, version: 2 } })) });

export const defaultOf = (templates) => templates.find(t => t.isDefault) || templates[0] || null;
